/**
 * Client-side hint solver for Word Labyrinth.
 *
 * Both the hint feature and the on-screen distance indicator must agree on
 * the shortest path between two words, so they share the same BFS here.
 *
 * `dictionary` is the loaded WordGraph (utils/wordGraph.js) or, in tests and for
 * legacy files, a plain object { "word": { "s": [...], "d": [...], "l": "lemma" } }.
 */
import { lemmaOf, MAX_SAME_LEMMA_RUN } from './lemmaRules';
import { asGraph } from './wordGraph';
import { splitLetters, upperWord, lowerWord } from './letters';
import { t } from '../i18n';

/**
 * Penalty factors for hints based on words removed
 */
const HINT_PENALTY_BASE = 0.75; // 25% penalty for adding word only
const HINT_PENALTY_REMOVE_1 = 0.60; // 40% penalty when removing 1 word
const HINT_PENALTY_REMOVE_2 = 0.50; // 50% penalty when removing 2 words
const HINT_PENALTY_REMOVE_N = 0.40; // 60% penalty when removing 3+ words
export const HINT_PENALTY_CLUE = 0.90; // 10% penalty for a clue: meaning and shape of the next word, not the word

// Hard cap on word length we'll ever traverse. The dictionary contains words
// well under 20 letters and the game's letter-box layout can't display longer
// rows anyway, so this is just a safety bound.
const ABSOLUTE_MIN_LENGTH = 3;
const ABSOLUTE_MAX_LENGTH = 20;

/**
 * Calculate penalty factor based on number of words to remove
 */
export function calculateHintPenalty(wordsToRemove) {
    if (wordsToRemove === 0) return HINT_PENALTY_BASE;
    if (wordsToRemove === 1) return HINT_PENALTY_REMOVE_1;
    if (wordsToRemove === 2) return HINT_PENALTY_REMOVE_2;
    return HINT_PENALTY_REMOVE_N;
}

/**
 * Plain forward BFS that returns the shortest path between two words as an
 * array `[from, ..., to]`, or null if no path exists within `maxDepth`.
 *
 * Words never get shorter (counted in letters of `context.language`): the board
 * only accepts changing or adding a letter, so a shrinking step is unplayable.
 *
 * Inflected languages: the path never runs through more than
 * MAX_SAME_LEMMA_RUN consecutive forms of one lemma. `context.beforeFrom` and
 * `context.afterTo` are the player's chain words around the searched stretch,
 * so the rule also holds where the path joins that chain. A search state is
 * therefore (word, run) rather than just the word, packed as index * 4 + run.
 */
function bfsShortestPath(graph, from, to, excludeWords, maxDepth, context = {}) {
    if (from === to) return [from];
    const fromIdx = graph.indexOf(from);
    const toIdx = graph.indexOf(to);
    if (fromIdx < 0 || toIdx < 0) return null;

    const excluded = new Set();
    for (const w of excludeWords) {
        const i = graph.indexOf(w);
        if (i >= 0) excluded.add(i);
    }
    const { beforeFrom = null, afterTo = null, language = 'en' } = context;
    const letterCounts = new Map();
    const lettersOf = (i) => {
        let n = letterCounts.get(i);
        if (n === undefined) {
            n = splitLetters(graph.words[i], language).length;
            letterCounts.set(i, n);
        }
        return n;
    };
    const startRun = beforeFrom && lemmaOf(graph, beforeFrom) === graph.lemmaIdOf(fromIdx) ? 2 : 1;
    const afterLemma = afterTo ? lemmaOf(graph, afterTo) : null;
    const toLemma = graph.lemmaIdOf(toIdx);

    // state -> parent state (-1 for the source): visited set and back-pointers in one.
    const startState = fromIdx * 4 + startRun;
    const parent = new Map([[startState, -1]]);
    let frontier = [startState];
    let depth = 0;

    while (frontier.length > 0 && depth < maxDepth) {
        depth++;
        const next = [];
        for (const state of frontier) {
            const i = state >> 2;
            const run = state & 3;
            const lemma = graph.lemmaIdOf(i);
            const neighbors = graph.neighborsOf(i);
            const minLetters = lettersOf(i);
            for (let k = 0; k < neighbors.length; k++) {
                const n = neighbors[k];
                const length = graph.words[n].length;
                if (length < ABSOLUTE_MIN_LENGTH || length > ABSOLUTE_MAX_LENGTH) continue;
                if (lettersOf(n) < minLetters) continue;
                const nRun = graph.lemmaIdOf(n) === lemma ? run + 1 : 1;
                if (nRun > MAX_SAME_LEMMA_RUN) continue;
                if (n === toIdx) {
                    // Joining the player's chain must not make a run of three either.
                    if (afterLemma !== null && afterLemma === toLemma && nRun >= MAX_SAME_LEMMA_RUN) continue;
                    const path = [to];
                    for (let cur = state; cur !== -1; cur = parent.get(cur)) path.push(graph.words[cur >> 2]);
                    return path.reverse();
                }
                if (excluded.has(n)) continue;
                const nState = n * 4 + nRun;
                if (parent.has(nState)) continue;
                parent.set(nState, state);
                next.push(nState);
            }
        }
        frontier = next;
    }
    return null;
}

/**
 * Build the exclude set for a path search: every word already on the player's
 * board (start, target, all guesses) except the two endpoints we're searching
 * between, since BFS itself needs to start on `from` and finish on `to`.
 */
function buildExcludeSet(from, to, previousGuesses, nextGuesses, startWord, targetWord) {
    const exclude = new Set();
    for (const w of previousGuesses) exclude.add(lowerWord(w));
    for (const w of nextGuesses) exclude.add(lowerWord(w));
    if (startWord) exclude.add(lowerWord(startWord));
    if (targetWord) exclude.add(lowerWord(targetWord));
    exclude.delete(from);
    exclude.delete(to);
    return exclude;
}

/**
 * The stretch to search given the player's (lowercase) chains: its endpoints and
 * the chain words just outside them, which the same-lemma rule has to respect.
 */
function chainContext(previousGuesses, nextGuesses, startWord, targetWord) {
    const from = previousGuesses.length > 0 ? previousGuesses[previousGuesses.length - 1] : startWord;
    const to = nextGuesses.length > 0 ? nextGuesses[0] : targetWord;
    const beforeFrom = previousGuesses.length > 1
        ? previousGuesses[previousGuesses.length - 2]
        : (previousGuesses.length === 1 ? startWord : null);
    const afterTo = nextGuesses.length > 1
        ? nextGuesses[1]
        : (nextGuesses.length === 1 ? targetWord : null);
    return { from, to, beforeFrom, afterTo };
}

/**
 * Find the best hint for the current game state.
 *
 * @param {Object} dictionary - The word dictionary with neighbor data
 * @param {string} startWord - Game start word
 * @param {string} targetWord - Game target word
 * @param {string[]} previousGuesses - Words guessed from the start
 * @param {string[]} nextGuesses - Words guessed from the target
 * @param {number} maxDepth - Maximum search depth (optional, default 20)
 * @param {string} language - Game language, for counting letters (optional, default 'en')
 * @returns {Object} - Hint result with success, hintType, hintWord, etc.
 */
export function findSmartHint(dictionary, startWord, targetWord, previousGuesses, nextGuesses, maxDepth = 20, language = 'en') {
    const graph = asGraph(dictionary);
    if (!graph) return { success: false, error: t('errors.wordNotFound') };
    startWord = lowerWord(startWord, language);
    targetWord = lowerWord(targetWord, language);
    previousGuesses = previousGuesses.map(w => lowerWord(w, language));
    nextGuesses = nextGuesses.map(w => lowerWord(w, language));

    const direct = chainContext(previousGuesses, nextGuesses, startWord, targetWord);
    const fromWord = direct.from;
    const toWord = direct.to;

    if (!graph.has(fromWord) || !graph.has(toWord)) {
        return { success: false, error: t('errors.wordNotFound') };
    }

    // First, try direct path with no removals.
    const directExclude = buildExcludeSet(fromWord, toWord, previousGuesses, nextGuesses, startWord, targetWord);
    const directPath = bfsShortestPath(graph, fromWord, toWord, directExclude, maxDepth, { ...direct, language });

    if (directPath && directPath.length >= 2) {
        return {
            success: true,
            hintType: 'add',
            hintWord: directPath[1],
            wordsToRemove: 0,
            removeFrom: null,
            direction: 'forward',
            penaltyFactor: calculateHintPenalty(0),
        };
    }

    // No direct path exists. Try removing words from the top (forward end of
    // the player's path) or bottom (backward end), preferring the shortest
    // resulting ladder. Up to 3 removals on each side.
    let bestOption = null;

    const considerCandidate = (path, wordsToRemove, removeFrom) => {
        if (!path || path.length < 2) return;
        const score = path.length + wordsToRemove; // total moves = additions + removals
        if (
            bestOption === null ||
            score < bestOption.score ||
            // Prefer removing from bottom on ties (matches prior behavior).
            (score === bestOption.score && bestOption.removeFrom === 'top' && removeFrom === 'bottom')
        ) {
            bestOption = {
                success: true,
                hintType: 'remove_add',
                hintWord: path[1],
                wordsToRemove,
                removeFrom,
                direction: 'forward',
                penaltyFactor: calculateHintPenalty(wordsToRemove),
                score,
            };
        }
    };

    const maxTopRemoval = Math.min(3, previousGuesses.length);
    for (let i = 1; i <= maxTopRemoval; i++) {
        const remainingPrev = previousGuesses.slice(0, -i);
        const ctx = chainContext(remainingPrev, nextGuesses, startWord, targetWord);
        if (!graph.has(ctx.from)) continue;

        const exclude = buildExcludeSet(ctx.from, ctx.to, remainingPrev, nextGuesses, startWord, targetWord);
        const tryPath = bfsShortestPath(graph, ctx.from, ctx.to, exclude, maxDepth, { ...ctx, language });
        considerCandidate(tryPath, i, 'top');
    }

    const maxBottomRemoval = Math.min(3, nextGuesses.length);
    for (let i = 1; i <= maxBottomRemoval; i++) {
        const remainingNext = nextGuesses.slice(i);
        const ctx = chainContext(previousGuesses, remainingNext, startWord, targetWord);
        if (!graph.has(ctx.to)) continue;

        const exclude = buildExcludeSet(ctx.from, ctx.to, previousGuesses, remainingNext, startWord, targetWord);
        const tryPath = bfsShortestPath(graph, ctx.from, ctx.to, exclude, maxDepth, { ...ctx, language });
        considerCandidate(tryPath, i, 'bottom');
    }

    if (bestOption) {
        const { score, ...result } = bestOption;
        return result;
    }

    return { success: false, error: t('errors.noPath') };
}

/**
 * Compute the shortest word-graph distance (edge count) between two words,
 * excluding all other words already in the player's path. Mirrors the BFS
 * used by findSmartHint's direct-path search so the displayed distance
 * matches what hints will actually find — they share `bfsShortestPath`.
 *
 * Returns the number of edges in the shortest path, or null if no path
 * exists within maxDepth (e.g. the player has dead-ended and would need to
 * remove words to make progress).
 */
export function findShortestPathLength(
    dictionary,
    fromWord,
    toWord,
    previousGuesses,
    nextGuesses,
    startWord,
    targetWord,
    maxDepth = 20,
    language = 'en'
) {
    const graph = asGraph(dictionary);
    if (!graph) return null;
    const from = lowerWord(fromWord || '', language);
    const to = lowerWord(toWord || '', language);
    if (!from || !to) return null;
    if (from === to) return 0;
    if (!graph.has(from) || !graph.has(to)) return null;

    const prev = previousGuesses.map(w => lowerWord(w, language));
    const next = nextGuesses.map(w => lowerWord(w, language));
    const exclude = buildExcludeSet(from, to, prev, next, startWord, targetWord);
    // The chain words outside the searched stretch matter for the same-lemma rule,
    // but only when the stretch is the gap between the player's two chains.
    const ctx = chainContext(prev, next, lowerWord(startWord || '', language), lowerWord(targetWord || '', language));
    const context = ctx.from === from && ctx.to === to ? { ...ctx, language } : { language };
    const path = bfsShortestPath(graph, from, to, exclude, maxDepth, context);
    return path === null ? null : path.length - 1;
}

/**
 * Build human-readable instruction from hint result, in the UI language.
 */
export function buildHintInstruction(hintResult, translate = t) {
    const word = upperWord(hintResult.hintWord);
    if (hintResult.hintType === 'add') return translate('game.hintInstruction.add', { word });
    const key = hintResult.removeFrom === 'top' ? 'removeTop' : 'removeBottom';
    return translate(`game.hintInstruction.${key}`, { count: hintResult.wordsToRemove, word });
}

/** True when two hint results point at the same next word with the same removal, so it was already paid for. */
export function isSameHint(a, b) {
    if (!a || !b) return false;
    return upperWord(a.hintWord) === upperWord(b.hintWord)
        && a.hintType === b.hintType
        && (a.wordsToRemove || 0) === (b.wordsToRemove || 0)
        && (a.removeFrom || null) === (b.removeFrom || null);
}

/** The chain word the hint word attaches to, once any removal the hint asks for is done. */
export function hintAnchorWord(hintResult, pathData) {
    const prev = hintResult.removeFrom === 'top'
        ? pathData.previousGuesses.slice(0, -hintResult.wordsToRemove)
        : pathData.previousGuesses;
    return prev.length > 0 ? prev[prev.length - 1] : pathData.startWord;
}

/** True when the text names the word, its lemma, or a longer form of either (so it would give the word away). */
export function mentionsWord(text, words, language = 'en') {
    const stems = words.filter(Boolean).map(w => lowerWord(w, language));
    if (!text || stems.length === 0) return false;
    return (text.match(/\p{L}+/gu) || []).some(token => {
        const low = lowerWord(token, language);
        return stems.some(s => low === s || (s.length >= 4 && low.startsWith(s)));
    });
}

/** Keep a clue readable: one gloss, cut at the first ';' when long, never past ~160 characters. */
export function shortenDefinition(text, max = 160) {
    let out = (text || '').trim();
    if (out.length > max / 2 && out.includes(';')) out = out.slice(0, out.indexOf(';')).trim();
    if (out.length > max) {
        const cut = out.lastIndexOf(' ', max);
        out = `${out.slice(0, cut > max / 2 ? cut : max).trim()}…`;
    }
    return out;
}

/** The first sense that doesn't restate the word ("something that is read" is no clue for READ), or null. */
export function pickClueDefinition(definitions, words, language = 'en') {
    for (const entry of definitions || []) {
        const text = shortenDefinition(entry?.definition);
        if (text && !mentionsWord(text, words, language)) return text;
    }
    return null;
}

/** The clue's extra line, in the UI language: what to remove first when the hint needs backing up. */
export function buildHintClueLines(hintResult, translate = t) {
    if (hintResult.hintType !== 'remove_add') return [];
    const key = hintResult.removeFrom === 'top' ? 'removeTop' : 'removeBottom';
    return [translate(`game.hintClue.${key}`, { count: hintResult.wordsToRemove })];
}
